<template>
    <div>
        <div v-if="errors.length" class="py-4 px-6 bg-red text-white mb-4">
            <ul class="mb-0" :class="{
                'pl-5': errors.length > 1,
                'list-none p-0': errors.length === 1,
                'text-center' : errors.length == 1
            }">
                <li v-for="error in errors" :key="error" v-html="error.replace(/(?:\r\n|\r|\n)/g, '<br />')"></li>
            </ul>
        </div>

        <div class="mx-auto w-20 h-20 rounded-full border-4 border-blue-450 overflow-hidden -mb-10 relative z-10">
            <img
                src="/img/avatar.svg"
                class="block w-full h-full bg-blue-450"
            />
        </div>

        <div class="bg-blue p-6 pt-12">
            <form class="pt-2" method="POST" action="/register" novalidate @submit="submit">
                <!-- CSRF -->
                <input type="hidden" name="_token" :value="token" />
                <input v-if="redirect" type="hidden" name="_redirect" :value="redirect" />

                <!-- Blank Field -->
                <div class="d-none" id="blank-id-wrapper">
                    <label>Keep this field blank</label>
                    <input type="text" name="blank-id" id="blank-id" value="" />
                </div>

                <!-- First Name -->
                <div class="form-group mb-4">
                    <label class="sr-only" for="email">Email</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text bg-blue-450 border-blue w-12">
                                <i class="far fa-user mx-auto" style="color: #ffffff !important;"></i>
                            </div>
                        </div>
                        <input
                            required
                            autofocus
                            id="firstname"
                            name="firstname"
                            type="text"
                            v-model="firstname"
                            placeholder="First Name"
                            @input="$v.firstname.$touch"
                            class="form-control p-3 border-blue text-white"
                            :class="{
                                'bg-red-400': fieldHasError('firstname'),
                                'bg-blue-450': fieldHasError('firstname') === false,
                            }"
                        />
                        <input
                            required
                            autofocus
                            id="lastname"
                            name="lastname"
                            type="text"
                            v-model="lastname"
                            placeholder="Last Name"
                            @input="$v.lastname.$touch"
                            class="form-control p-3 border-blue text-white"
                            :class="{
                                'bg-red-400': fieldHasError('lastname'),
                                'bg-blue-450': fieldHasError('lastname') === false,
                            }"
                        />
                    </div>
                </div>

                <!-- Email -->
                <div class="form-group mb-4">
                    <label class="sr-only" for="email">Email</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text bg-blue-450 border-blue w-12">
                                <i class="far fa-envelope mx-auto" style="color: #ffffff !important;"></i>
                            </div>
                        </div>
                        <input
                            required
                            id="email"
                            name="email"
                            type="email"
                            v-model="email"
                            dusk="login-email"
                            placeholder="Email"
                            @blur="$v.email.$touch"
                            class="form-control p-3 border-blue text-white"
                            :class="{
                                'bg-red-400': fieldHasError('email'),
                                'bg-blue-450': fieldHasError('email') === false,
                            }"
                        />
                        <div v-if="rbaError" class="text-sm w-full mt-2 text-white">
                            <i class="fas fa-exclamation-triangle text-red-400"></i>
                            For security reasons, role-based email addresses (e.g., support@, admin@) are not permitted for logging into the MySAU portal. Please use a personal email address instead.
                        </div>
                    </div>
                </div>

                <!-- Password -->
                <div class="form-group mb-8">
                    <label class="sr-only" for="password">Password</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text bg-blue-450 border-blue w-12">
                                <i class="far fa-lock-alt mx-auto" style="color: #ffffff !important;"></i>
                            </div>
                        </div>
                        <input
                            required
                            id="password"
                            name="password"
                            :type="passwordInputType"
                            v-model="password"
                            @input="$v.password.$touch"
                            placeholder="Create a password"
                            class="form-control p-3 border-blue text-white"
                            :class="{
                                'bg-red-400': fieldHasError('password'),
                                'bg-blue-450': fieldHasError('password') === false,
                            }"
                        />
                    </div>

                    <password-strength :password="password" class="border border-blue pso" error-text-class="text-blue-100"></password-strength>

                    <div class="text-right text-blue-200 text-sm">
                        <button class="appearance-none outline-none" @click.prevent="togglePasswordVisibility()">
                            <i class="fas fa-eye" :class="{ 'fa-eye-slash': showPassword , 'fa-eye': !showPassword}"></i> {{ showPassword ? 'HIDE' : 'SHOW' }}
                        </button>
                    </div>
                </div>

                <!-- Recaptcha -->
                <vue-recaptcha class="mb-4" ref="recaptcha" @verify="recaptchaVerify" :sitekey="recaptchasitekey"></vue-recaptcha>

                <!-- Message -->
                <div class="form-row" v-if="message">
                    <div class="col">
                        <p class="text-danger mb-0">{{ message }}</p>
                    </div>
                </div>

                <!-- Button -->
                <div class="form-row">
                    <div class="col">
                        <button
                            type="submit"
                            dusk="login-button"
                            class="btn btn-block btn-lg"
                            :class="{
                                'btn-success': canSubmit,
                                'btn-light cursor-not-allowed': cannotSubmit,
                            }"
                        >
                            Register <i v-if="submitting" class="far fa-spinner-third fa-spin"></i>
                        </button>
                        <div class="col pt-1 text-center text-blue-200 text-xs">
                            By registering, you agree to the <a href="https://www.serversaustralia.com.au/about-us/legal/privacy-policy.php" class="text-white no-underline hover:underline" target="_blank">Terms of Service and Privacy Policy</a>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import { email, required } from 'vuelidate/lib/validators';
import PasswordStrength from '@js/plugins/password-strength/PasswordStrength.vue';

export default {
    components: {
        VueRecaptcha,
        PasswordStrength,
    },

    props: {
        old: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        token: String,
        errors: Array,
        message: String,
        redirect: String,
        recaptchasitekey: String,
        rolebasedaddresses: {
            required: false,
            type: Array,
            default: function () {
                return [];
            },
        },
    },

    data: function () {
        return {
            firstname: this.old.firstname ? this.old.firstname : '',
            lastname: this.old.lastname ? this.old.lastname : '',
            email: this.old.email ? this.old.email : '',
            password: '',
            recaptchaResponse: '',
            submitting: false,
            showPassword: false,
            rbaError: false,
        }
    },

    validations: {
        firstname: { required },
        lastname: { required },
        email: { required, email,
            rbaDisallowed: function(value) {
                const roleBasedEmailAddresses = this.$props.rolebasedaddresses;
                console.log(roleBasedEmailAddresses);
                if(roleBasedEmailAddresses.some(email => value.startsWith(email))) {
                    this.rbaError = true;
                    return false;
                }

                this.rbaError = false;
                return true;
            },
         },
        password: { required },
    },

    computed: {
        canSubmit() {
            return ! this.$v.$invalid
                && !! this.recaptchaResponse;
        },

        cannotSubmit() {
            return this.canSubmit === false;
        },

        passwordInputType() {
            return this.showPassword ? 'text' : 'password';
        }
    },

    methods: {
        recaptchaVerify: function (response) {
            this.recaptchaResponse = response;
        },

        resetRecaptcha: function () {
            this.$refs.recaptcha.reset();
        },

        fieldHasError: function (fieldName) {
            return this.$v[fieldName].$error || (
                this.$v[fieldName].$dirty && ! this.$v[fieldName].required
            );
        },

        submit: function (event) {
            if (this.submitting || this.cannotSubmit) {
                event.preventDefault();
                return;
            }

            this.submitting = true;
        },

        togglePasswordVisibility() {
            return this.showPassword = !this.showPassword;
        }
    },
}
</script>